import { useEffect, useMemo, useState } from 'react';
import css from './index.module.scss';
import {
  DataGrid,
  DocumentRow,
  NoDocumentPlaceholder,
  PaginationScroller,
  PopOverButton
} from '@components/common';
import { CellProps } from 'react-table';
import { openDocumentSignedLink, sanitizeValue } from '@helpers/utils';
import { Button, IconNode, ToolTip, Typography } from '@components/base';
import Images from '@assets/images';
import AddProductDocuments from '../add-product-documents';
import { Popover } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import { IDocumentTabState } from '../..';

interface SupplierProductDocumentsProps {
  getProductDocumentsList: (params: any) => Promise<void>;
  getProductDocumentFilterOptions: () => Promise<void>;
  setDocumentTabState: React.Dispatch<React.SetStateAction<IDocumentTabState>>;
  documentTabState: IDocumentTabState;
}

const SupplierProductDocuments = (props: SupplierProductDocumentsProps) => {
  const {
    getProductDocumentsList,
    getProductDocumentFilterOptions,
    setDocumentTabState,
    documentTabState
  } = props;
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    productDocumentsList,
    openAddProductDocumentModal,
    productDocumentData,
    viewDocumentDescriptionData,
    currentPage,
    itemLimit,
    count,
    isLoading
  } = documentTabState;

  useEffect(() => {
    getProductDocumentFilterOptions();
    getProductDocumentsList({ page: currentPage });
  }, []);

  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setIsPopoverOpen(false);
  };

  const [documentColumn, documentData] = useMemo(() => {
    const column: any = [
      {
        Header: 'Document Name',
        accessor: 'document_name',
        Cell: (props: CellProps<any>) => {
          const { value } = props;
          const description = value?.document_description;
          return (
            <div className={css.datagridDocumentName}>
              {sanitizeValue(value?.document_name)}
              {description && (
                <Button
                  variant="text"
                  onClick={(event: any) => {
                    setDocumentTabState((prevState: IDocumentTabState) => ({
                      ...prevState,
                      viewDocumentDescriptionData: description
                    }));
                    handlePopoverOpen(event);
                  }}>
                  Description
                </Button>
              )}
            </div>
          );
        }
      },
      {
        Header: 'Product Name',
        accessor: 'product_name',
        Cell: (props: CellProps<any>) => {
          const { value } = props;
          const documentName = `${value.product_name} - ${value.grade}`;
          if (documentName)
            return (
              <div className={css.datagridProductName}>
                <DocumentRow.Title
                  title={documentName}
                  documentAvailable={value?.document_object}
                />
              </div>
            );
          return null;
        }
      },
      {
        Header: 'Document Type',
        accessor: 'document_type',
        Cell: (props: CellProps<any>) => {
          const { value } = props;
          if (value) return <div>{value}</div>;
          return null;
        }
      },
      {
        Header: '',
        accessor: 'document_link',
        Cell: (props: CellProps<any>) => {
          const { value } = props;
          const pdfURL = value?.document;
          const remarks = value?.remarks;

          return (
            <div className={css.actionWrapper}>
              {remarks && (
                <ToolTip title={remarks}>
                  <div>
                    <IconNode src={Images.infoGrey} alt="Remarks" style={{ cursor: 'pointer' }} />
                  </div>
                </ToolTip>
              )}
              {pdfURL && (
                <ToolTip title="View Document">
                  <div>
                    <IconNode
                      src={Images.eyeRed}
                      alt="PDF Download"
                      onClick={() => {
                        openDocumentSignedLink(pdfURL);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </ToolTip>
              )}
              <ToolTip title="Edit">
                <div>
                  <IconNode
                    src={Images.editRed}
                    alt="Edit"
                    onClick={() => {
                      setDocumentTabState((prevState: IDocumentTabState) => ({
                        ...prevState,
                        productDocumentData: value,
                        openAddProductDocumentModal: true
                      }));
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </ToolTip>
            </div>
          );
        }
      }
    ];

    const row: any = (productDocumentsList || []).map((prod: any) => ({
      document_name: prod,
      product_name: prod,
      document_type: prod.doc_type_display,
      document_link: prod,
      document_description: prod.document_description
    }));
    return [column, row];
  }, [productDocumentsList]);

  const validatePageChange = (event: any, pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > Math.ceil(count / itemLimit)) return;

    // Update state
    setDocumentTabState((prevState: IDocumentTabState) => ({
      ...prevState,
      currentPage: pageNumber
    }));

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', pageNumber.toString());

    const activeSubTab = searchParams.get('activeSubTab');
    if (activeSubTab) {
      searchParams.set('activeSubTab', activeSubTab);
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString()
      },
      { replace: true }
    );

    getProductDocumentsList({ page: pageNumber });
  };

  return (
    <div className={css.mainWrapper}>
      <div className={css.header}>
        <Typography variant="h4">Product Documents</Typography>
        <Button
          onClick={() => {
            setDocumentTabState((prevState: IDocumentTabState) => ({
              ...prevState,
              openAddProductDocumentModal: true,
              productDocumentData: null
            }));
          }}>
          Add Documents
        </Button>
        {/* <PaginationNavigator
          currentPageNumber={currentPage}
          orderCount={count}
          itemLimit={itemLimit}
          validatePageChange={validatePageChange}
        /> */}
      </div>
      {productDocumentsList?.length > 0 ? (
        <DataGrid columns={documentColumn} data={documentData} />
      ) : (
        !isLoading && (
          <NoDocumentPlaceholder
            title="No Documents added yet"
            supportingText="When a Document is added by you, they will show up here."
            buttonText="Add Document"
            onClick={() => {
              setDocumentTabState((prevState: IDocumentTabState) => ({
                ...prevState,
                openAddProductDocumentModal: true,
                productDocumentData: null
              }));
            }}
          />
        )
      )}

      {!isLoading && (
        <PaginationScroller
          variant="text"
          defaultPage={1}
          count={count}
          pageLimit={itemLimit}
          page={currentPage}
          onChange={validatePageChange}
        />
      )}
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}>
        <div
          style={{
            height: '60px',
            padding: '20px'
          }}>
          {viewDocumentDescriptionData || 'No description available'}
        </div>
      </Popover>

      {openAddProductDocumentModal && (
        <AddProductDocuments
          open={openAddProductDocumentModal}
          onClose={() => {
            setDocumentTabState((prevState: IDocumentTabState) => ({
              ...prevState,
              openAddProductDocumentModal: false,
              getProductDocumentsList: null
            }));
          }}
          data={productDocumentData}
          documentTabState={documentTabState}
          setDocumentTabState={setDocumentTabState}
          getProductDocumentFilterOptions={getProductDocumentFilterOptions}
          getProductDocumentsList={getProductDocumentsList}
        />
      )}
    </div>
  );
};

export default SupplierProductDocuments;

const PaginationNavigator = (props: any) => {
  const { currentPageNumber, orderCount, itemLimit, validatePageChange } = props;
  return (
    <div className={css.paginationFilter}>
      <ToolTip title="Previous Page">
        <ArrowBackIosIcon
          sx={{
            height: '40px',
            cursor: currentPageNumber > 1 ? 'pointer' : 'default',
            color: currentPageNumber > 1 ? '#000000' : '#e2e2e2'
          }}
          onClick={() => validatePageChange(null, currentPageNumber - 1)}
        />
      </ToolTip>
      <Typography className={css.pageOfTotalPage}>
        {currentPageNumber} of {Math.ceil(orderCount / itemLimit)}
      </Typography>
      <ToolTip title="Next Page">
        <ArrowForwardIosIcon
          sx={{
            height: '40px',
            cursor: currentPageNumber < Math.ceil(orderCount / itemLimit) ? 'pointer' : 'default',
            color: currentPageNumber < Math.ceil(orderCount / itemLimit) ? '#000000' : '#e2e2e2'
          }}
          onClick={() => validatePageChange(null, currentPageNumber + 1)}
        />
      </ToolTip>
    </div>
  );
};
